import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  HostListener,
  inject,
  input,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  IsActiveMatchOptions,
  RouterLink,
  RouterLinkActive,
  UrlTree,
} from '@angular/router';
import { AiBorderDirective } from '@catch/directives';
import { ConnectionService } from '@catch/services';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { SpinnerComponent } from '../spinner/spinner.component';
import {
  BUTTON_VARIANT_CSS_SUFFIX,
  type ButtonHtmlType,
  type ButtonVariant,
} from './button.types';

export type { ButtonVariant } from './button.types';

export type ButtonRouterLinkInput =
  | string
  | readonly (string | number | boolean)[]
  | UrlTree
  | null;

@Component({
  selector: 'app-button',
  imports: [
    NgClass,
    SpinnerComponent,
    RouterLink,
    RouterLinkActive,
    FaIconComponent,
    AiBorderDirective,
  ],
  template: `
    @if (variant() === 'ai') {
      <div appAiBorder [radius]="rounded() ? '500px' : undefined"></div>
    }
    <button
      [attr.type]="effectiveHtmlType()"
      [routerLink]="routerLink() ?? null"
      [routerLinkActive]="routerLinkActiveDirectiveInput()"
      [routerLinkActiveOptions]="routerLinkActiveOptionsBinding()"
      class="ct-btn"
      [ngClass]="buttonClasses()"
      [disabled]="isDisabled()"
      [attr.aria-busy]="loading() ? true : null"
      [attr.aria-label]="ariaLabel() ?? null"
    >
      @if (iconStart(); as startIcon) {
        <fa-icon [icon]="startIcon" aria-hidden="true" class="start-icon"></fa-icon>
      }
      <span class="ct-app-button-label">
        <ng-content></ng-content>
      </span>
      @if (iconEnd(); as endIcon) {
        <fa-icon [icon]="endIcon" aria-hidden="true" class="end-icon"></fa-icon>
      }
      @if (loading()) {
        <ct-lib-spinner></ct-lib-spinner>
      }
    </button>
  `,
  styleUrl: './button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'ct-app-button-host',
    '[class.ct-app-button-full-width]': 'fullWidth()',
    '[class.ct-app-button-disabled]': 'isDisabled()',
  },
})
export class ButtonComponent {
  private readonly connectionService = inject(ConnectionService);
  private readonly connected = toSignal(this.connectionService.connected$, {
    initialValue: true,
  });

  readonly variant = input<ButtonVariant>('primary');
  readonly htmlType = input<ButtonHtmlType>('button');
  readonly disabled = input(false);
  readonly loading = input(false);
  readonly round = input(false);
  readonly small = input(false);
  readonly rounded = input(false);
  readonly active = input(false);
  readonly iconStart = input<IconProp | undefined>(undefined);
  readonly iconEnd = input<IconProp | undefined>(undefined);
  readonly fullWidth = input<boolean>(false);
  readonly ariaLabel = input<string | undefined>(undefined);
  readonly disableOnNoConnection = input(false);
  readonly routerLink = input<ButtonRouterLinkInput | undefined>(undefined);
  readonly routerLinkActive = input<string | readonly string[] | undefined>(
    undefined
  );
  readonly routerLinkActiveOptions = input<
    | { exact: boolean }
    | Partial<IsActiveMatchOptions>
    | undefined
  >();

  readonly ctBtnStyleSuffix = computed(
    () => BUTTON_VARIANT_CSS_SUFFIX[this.variant()]
  );

  readonly effectiveHtmlType = computed<ButtonHtmlType>(() => {
    if (this.routerLink() != null) {
      return 'button';
    }
    return this.htmlType();
  });

  readonly isOfflineBlocked = computed(
    () => this.disableOnNoConnection() && !this.connected()
  );

  readonly isDisabled = computed(
    () => this.disabled() || this.loading() || this.isOfflineBlocked()
  );

  readonly buttonClasses = computed(() => {
    const suffix = this.ctBtnStyleSuffix();
    const classes: Record<string, boolean> = {
      [`ct-btn-${suffix}`]: true,
      loading: this.loading(),
      'ct-btn-round': this.round(),
      'ct-app-button-small': this.small(),
      'ct-btn-rounded': this.rounded(),
      'ct-app-button-has-icon-start': this.iconStart() != null,
      'ct-app-button-has-icon-end': this.iconEnd() != null,
      active: this.active(),
      'ct-app-button-full-width': this.fullWidth(),
    };
    return classes;
  });

  readonly routerLinkActiveDirectiveInput = computed((): string | string[] => {
    if (this.routerLink() == null) {
      return '';
    }
    const value = this.routerLinkActive() ?? 'active';
    return value as string | string[];
  });

  readonly routerLinkActiveOptionsBinding = computed(():
    | { exact: boolean }
    | Partial<IsActiveMatchOptions> => {
    if (this.routerLink() == null) {
      return { exact: false };
    }
    return this.routerLinkActiveOptions() ?? { exact: false };
  });

  @HostListener('click', ['$event'])
  onHostClick(event: Event): void {
    if (this.isDisabled()) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }
}
